import React, {useState, useEffect, useCallback} from 'react';
import styled from 'styled-components';
import {debounce} from 'lodash';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import IconButton from '@material-ui/core/IconButton';
import ClearRoundedIcon from '@material-ui/icons/ClearRounded';
import Tune from '@material-ui/icons/Tune';
import CircularProgress from '@material-ui/core/CircularProgress';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import SiteIdSearchSuggestions from './SiteIdSearchSuggestions';
import searchAndSort from './functions/searchAndSort';
import Badge from '@material-ui/core/Badge';
import {useHistory} from 'react-router-dom';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';

//#region styled-components
const SiteIdSearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  &.left {
    align-items: flex-start;
    .site-type-selection {
      margin-left: 4px;
    }
  }

  &.right {
    align-items: flex-end;
  }
`;

const StyledTextFieldContainer = styled.div`
  /* height: 32px; */
  position: relative;
  z-index: 11;
`;

const StyledTextField = styled(TextField)`
  .MuiFilledInput-root {
    background-color: ${props => props.theme.white};
    color: ${props => props.theme.black};
    border-radius: 70px;
    border: 1px solid ${props => props.theme.text};
    box-shadow: 0px 4px 24px 0px #00000026;
  }
  &.search-active .MuiFilledInput-root {
    border-radius: 35px 35px 0px 0px;
  }
  .MuiInputAdornment-filled.MuiInputAdornment-positionStart:not(.MuiInputAdornment-hiddenLabel) {
    margin-top: 1px;
  }
  .MuiFilledInput-input {
    padding: 0px 11px 0px 11px;
    font-size: 1rem;
    width: 500px;
    height: 64px;
  }
  .MuiFilledInput-underline:after,
  .MuiFilledInput-underline:before {
    border-bottom: none;
  }
`;

const StyledIconButton = styled(IconButton)`
  padding: 0px 0px 0px;
  width: 24px;
  height: 24px;
  margin-right: 8px;
  color: ${props => props.theme.text};
  &.Mui-disabled {
    opacity: 0;
  }
  &:hover {
    color: ${props => props.theme.subtleText};
  }
`;

const StyledTuneButton = styled(IconButton)`
  padding: 0px 0px 0px;
  width: 34px;
  height: 34px;
  &.filter-active {
    background-color: ${props => props.theme.activeStateBlue};
  }
  &.filter-inactive {
    background-color: #d9d9d9;
  }
  &:hover {
    color: ${props => props.theme.subtleText};
    background-color: ${props => props.theme.activeStateBlue}80;
  }
`;

const StyledSearchIcon = styled(SearchRoundedIcon)`
  color: ${props => props.theme.disabledStateBlue};
`;

const StyledClearRoundedIcon = styled(ClearRoundedIcon)`
  color: ${props => props.theme.disabledStateBlue};
`;

const StyledTuneIcon = styled(Tune)`
  color: ${props => props.theme.disabledStateBlue};
  &.filter-active {
    color: ${props => props.theme.defaultNavy};
  }
`;

const StyledCircleLoading = styled(CircularProgress)`
  height: 24px;
  width: 24px;
  svg {
    color: ${props => props.theme.disabledStateBlue};
  }
`;

const StyledAdornmentBegin = styled(InputAdornment)`
  margin: 0px 10px 0px 10px;
`;
//#endregion styled-components

interface SiteIdSearchProps {
  clickHandler: (site: Site) => void;
  filterClickedHandler: () => void;
  searchSuggestions: Site[];
  allDataIsLoaded: boolean;
  filterOptions: Record<string, string>;
  isFilterActive: boolean;
}

export default function SearchInput({
  clickHandler,
  filterClickedHandler,
  searchSuggestions,
  allDataIsLoaded,
  filterOptions,
  isFilterActive,
}: SiteIdSearchProps) {
  const history = useHistory();
  //#region localState
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [suggestedSearchItems, setSuggestedSearchItems] = useState<Site[]>([]);
  const [filteredSearchValue, setFilteredSearchValue] = useState<string>('');
  //#endregion localState

  const allDataIsFilled = filteredSearchValue.length > 0 && searchSuggestions.length > 0;
  const filterOptionsLength = Object.keys(filterOptions).length;

  //#region handles
  const clearSearch = () => {
    setSearchValue('');
    setSuggestedSearchItems([]);
  };

  const debouncedSetFilteredSearchValue = useCallback(
    debounce((searchValue: string) => {
      const filteredSearchValue = searchValue.toLowerCase();
      setFilteredSearchValue(filteredSearchValue);
    }, 150),
    []
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleFocus = () => {
    setIsOpen(true);
  };

  const handleClickAway = () => {
    setIsOpen(false);
  };

  const handleViewAllClick = () => {
    history.push(`/npos?viewAll=${searchValue}`);
  };
  //#endregion handles

  //#region useEffect
  useEffect(() => {
    debouncedSetFilteredSearchValue(searchValue);
    return () => {
      debouncedSetFilteredSearchValue.cancel();
    };
  }, [searchValue, debouncedSetFilteredSearchValue]);

  useEffect(() => {
    if (allDataIsLoaded && allDataIsFilled) {
      const filteredSites = searchAndSort({
        filteredSearchValue,
        searchSuggestions,
        filterOptions,
      });
      setSuggestedSearchItems(filteredSites);
    } else {
      clearSearch();
    }
  }, [searchSuggestions, filteredSearchValue, filterOptions, allDataIsLoaded, allDataIsFilled]);
  //#endregion useEffect

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <SiteIdSearchContainer id="siteid-search-container">
        <StyledTextFieldContainer>
          <>
            <StyledTextField
              disabled={!allDataIsLoaded}
              id="siteid-search"
              name="siteid-search"
              aria-describedby="site-id-search"
              variant="filled"
              onFocus={handleFocus}
              className={isOpen && suggestedSearchItems.length > 0 ? 'search-active' : ''}
              onChange={handleChange}
              placeholder={'Site name, store #, name, etc.'}
              autoComplete={'off'}
              value={searchValue}
              InputProps={{
                startAdornment: (
                  <StyledAdornmentBegin position="start">
                    {allDataIsLoaded ? <StyledSearchIcon /> : <StyledCircleLoading size={24} />}
                  </StyledAdornmentBegin>
                ),
                endAdornment: (
                  <>
                    <InputAdornment position="end">
                      <StyledIconButton
                        disabled={!(searchValue.length > 0)}
                        aria-label="clear search field"
                        onClick={clearSearch}
                      >
                        <StyledClearRoundedIcon />
                      </StyledIconButton>
                      <StyledTuneButton
                        aria-label="filter search field"
                        className={isFilterActive ? 'filter-active' : 'filter-inactive'}
                        onClick={filterClickedHandler}
                      >
                        <Badge
                          badgeContent={filterOptionsLength}
                          invisible={filterOptionsLength === 0}
                          color="primary"
                        >
                          <StyledTuneIcon className={isFilterActive ? 'filter-active' : ''} />
                        </Badge>
                      </StyledTuneButton>
                    </InputAdornment>
                  </>
                ),
              }}
            />
            {isOpen && (
              <SiteIdSearchSuggestions
                suggestedSearchItems={suggestedSearchItems}
                clearUserInput={clearSearch}
                clickHandler={clickHandler}
                handleViewAllClick={handleViewAllClick}
              />
            )}
          </>
        </StyledTextFieldContainer>
      </SiteIdSearchContainer>
    </ClickAwayListener>
  );
}
